import React, { useState } from "react";
import ScheduledTaskModal from "./ScheduledTaskModal";
import ScheduledTaskLogsModal from "./ScheduledTaskLogsModal";
import McpRequiredModal from "./McpRequiredModal";
import { IconClock, IconInfo, IconPlay } from "./Icons";
import "./ScheduledTasksPanel.css";

const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
// A touch over the server's 30-min run cap (RUN_TIMEOUT_SECONDS).
const RUN_TIMEOUT_MS = 31 * 60 * 1000;

function fmtTime(hour, minute) {
  const ampm = hour >= 12 ? "PM" : "AM";
  const hh = hour % 12 === 0 ? 12 : hour % 12;
  return `${hh}:${String(minute).padStart(2, "0")} ${ampm}`;
}

const DAY_LONG = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const WEEK_LABEL = { 1: "First", 2: "Second", 3: "Third", 4: "Fourth", "-1": "Last" };

function ordinal(n) {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function scheduleSummary(t) {
  let dayPart;
  const kind = t.schedule_kind || "weekly";
  if (kind === "monthly") {
    const days = [...(t.days_of_month || [])].sort((a, b) => a - b);
    const labels = days.filter((d) => d > 0).map(ordinal);
    if (days.includes(-1)) labels.push("last day");
    dayPart = `Monthly on the ${labels.join(", ")}`;
  } else if (kind === "monthly_weekday") {
    dayPart = `${WEEK_LABEL[t.week_of_month]} ${DAY_LONG[t.weekday]} of the month`;
  } else {
    const days = [...(t.days_of_week || [])].sort((a, b) => a - b);
    if (days.length === 7) dayPart = "Every day";
    else if (days.join(",") === "0,1,2,3,4") dayPart = "Weekdays";
    else dayPart = days.map((d) => DAY_SHORT[d]).join(" · ");
  }
  return `${dayPart} — ${fmtTime(t.hour, t.minute)}`;
}

// In flight while last_run_status is "started" and recent; past the run cap a
// "started" run is interrupted, not live.
function isRunning(t) {
  return t.last_run_status === "started" && !!t.last_run_at &&
    Date.now() - new Date(t.last_run_at).getTime() < RUN_TIMEOUT_MS;
}

// The info icon turns red when the last run failed OR was interrupted (a
// "started" run older than the run cap — i.e. the server died mid-run).
function lastRunFailed(t) {
  if (t.last_run_status === "error") return true;
  return t.last_run_status === "started" && !!t.last_run_at &&
    Date.now() - new Date(t.last_run_at).getTime() >= RUN_TIMEOUT_MS;
}

/** Right-column Scheduled Tasks card: per-user tasks in the active env. */
export default function ScheduledTasksPanel({
  tasks, loading, error, envId, agents, models,
  category, onCategoryChange, isTopManager, mcpServers, onConnectMcp,
  onCreate, onUpdate, onDelete, onRunNow, onOpenThread,
}) {
  const [modal, setModal] = useState(null);       // null | { task: task|null }
  const [logsTask, setLogsTask] = useState(null); // task whose logs are open
  const [actionError, setActionError] = useState(null);
  // {taskId, action: "enable" | "run", missing: [{name,url}]} | null
  const [mcpGate, setMcpGate] = useState(null);

  const tryAction = (p) => {
    setActionError(null);
    Promise.resolve(p).catch((e) => setActionError(e.message || "Action failed"));
  };

  // Enable/run attempts can 409 with the list of missing MCP servers — that
  // opens the connect gate instead of the inline error strip.
  const tryGatedAction = (taskId, action, p) => {
    setActionError(null);
    Promise.resolve(p).catch((e) => {
      if (e.status === 409 && e.data?.missing_servers) {
        setMcpGate({ taskId, action, missing: e.data.missing_servers });
      } else {
        setActionError(e.message || "Action failed");
      }
    });
  };

  const retryGated = async () => {
    const { taskId, action } = mcpGate;
    try {
      if (action === "run") await onRunNow(taskId);
      else await onUpdate(taskId, { enabled: true });
      setMcpGate(null);
    } catch (e) {
      if (e.status === 409 && e.data?.missing_servers) {
        setMcpGate((g) => ({ ...g, missing: e.data.missing_servers }));
      } else {
        setMcpGate(null);
        setActionError(e.message || "Action failed");
      }
    }
  };

  return (
    <div className="ca-sched">
      <div className="ca-sched-tabs" role="tablist" aria-label="Task category">
        {[["personal", "Personal"], ["lt", "LT"]].map(([value, label]) => (
          <button key={value} type="button" role="tab"
                  aria-selected={category === value}
                  className={`ca-sched-tab${category === value ? " on" : ""}`}
                  onClick={() => onCategoryChange(value)}>
            {label}
          </button>
        ))}
      </div>
      {(category !== "lt" || isTopManager) && (
        <button
          className="ca-btn ca-btn--sm ca-btn--block ca-sched-add"
          disabled={!envId}
          onClick={() => setModal({ task: null })}
        >
          + New scheduled task
        </button>
      )}
      {(actionError || error) && (
        <div className="ca-sched-error">{actionError || error.message || "Failed to load"}</div>
      )}
      {loading && <div className="ca-sched-empty">Loading…</div>}
      {!loading && tasks.length === 0 && (
        <div className="ca-sched-empty">
          {category === "lt"
            ? "No LT tasks yet."
            : "No scheduled tasks yet. They run server-side — even with your computer off."}
        </div>
      )}
      <ul className="ca-sched-list">
        {tasks.map((t) => {
          const running = isRunning(t);
          return (
            <li key={t.id} className={`ca-sched-row${t.enabled ? "" : " paused"}`}>
              <div className="ca-sched-row-top">
                <div className="ca-sched-meta">
                  <IconClock size={13} />
                  <span className="ca-sched-name" title={t.message}>{t.name}</span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={t.enabled}
                  className={`ca-switch${t.enabled ? " on" : ""}`}
                  onClick={() => t.enabled
                    ? tryAction(onUpdate(t.id, { enabled: false }))
                    : tryGatedAction(t.id, "enable", onUpdate(t.id, { enabled: true }))}
                  title={t.enabled ? "Enabled — runs on schedule" : "Paused"}
                >
                  <span className="ca-switch-knob" />
                </button>
              </div>
              <div className="ca-sched-summary">{scheduleSummary(t)}</div>
              <div className="ca-sched-row-actions">
                <button
                  type="button"
                  className={`ca-sched-icon-btn${lastRunFailed(t) ? " err" : ""}`}
                  title="Run log"
                  aria-label="View run log"
                  onClick={() => setLogsTask(t)}
                >
                  <IconInfo size={14} />
                </button>
                <button
                  type="button"
                  className="ca-btn ca-btn--xs ca-btn--ghost ca-sched-run"
                  disabled={running}
                  title={running ? "A run is in progress" : "Run now"}
                  onClick={() => tryGatedAction(t.id, "run", onRunNow(t.id))}
                >
                  <IconPlay size={12} /> {running ? "Running…" : "Run now"}
                </button>
                {(category !== "lt" || isTopManager) && (
                  <>
                    <button className="ca-btn ca-btn--xs ca-btn--ghost"
                            onClick={() => setModal({ task: t })}>Edit</button>
                    <button className="ca-btn ca-btn--xs ca-btn--ghost ca-sched-delete"
                            onClick={() => tryAction(onDelete(t.id))}>Delete</button>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {modal && (
        <ScheduledTaskModal
          task={modal.task}
          agents={agents}
          models={models}
          defaultCategory={category}
          isTopManager={isTopManager}
          mcpServers={mcpServers}
          onClose={() => setModal(null)}
          onSave={async (body) => {
            if (modal.task) await onUpdate(modal.task.id, body);
            else await onCreate(body);
            setModal(null);
          }}
        />
      )}
      {mcpGate && (
        <McpRequiredModal
          missing={mcpGate.missing}
          servers={mcpServers}
          action={mcpGate.action}
          onConnect={onConnectMcp}
          onRetry={retryGated}
          onClose={() => setMcpGate(null)}
        />
      )}
      {logsTask && (
        <ScheduledTaskLogsModal
          envId={envId}
          task={logsTask}
          onClose={() => setLogsTask(null)}
          onOpenThread={onOpenThread}
        />
      )}
    </div>
  );
}
